import React, { useContext, useEffect, useState } from 'react';
import { observer, useLocalStore } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { withErrorBoundary } from 'react-error-boundary';
import {
  Alert,
  AlertGroup,
  AlertActionCloseButton,
  AlertVariant,
  AlertActionLink,
} from '@patternfly/react-core';
import { useLocalStorageState, useFeature } from '@ole-ui/ole-ui-components';
import { Helmet } from 'react-helmet';
import { storesContext } from 'stores';
import { handleError } from 'services/ErrorService';
import GenericViewError from 'components/Error/GenericViewError';
import LearningPathWidget from 'components/LearningPath/Widget';
import SubscriptionWidget from 'components/Subscriptions/LineWidget';
import HeroCarousel from 'components/HeroCarousel';
import ClassesWidget from 'components/Classes/Widget';
import RHLSTrialWidget from 'components/RHLSTrialWidget';
import NoEnrollmentFound from 'components/NoEnrollment/NoEnrollmentFound';
import FreeSkillsAssessment from 'components/NoEnrollment/FreeSkillsAssessment';
import AccessLearningSubscription from 'components/NoEnrollment/AccessLearningSubscription';
import SubscriptionsVideo from 'components/NoEnrollment/SubscriptionsVideo';
import MyProgressWidget from 'components/MyProgress';
import PendingManagerForm from 'components/Dashboard/PendingManagerForm';
import ExploreSubscriptions from 'components/ExploreSubscriptions';
import VideoTourDisplay from 'components/Banners/VideoTourDisplay';
import FreeTrialComparisonTable from 'components/FreeTrialComparisonTable';
import { EnablementFeatureFlags } from 'config/featureFlags';
import HomeStore from './store';

import './styles.scss';
import FreeTrialOffering from './FreeTrialOffering';

const GenericAlert = React.lazy(() =>
  import(
    /* webpackChunkName: 'generic-alert' */ 'components/Banners/GenericAlert'
  ),
);

const ComplianceBannerDialog = React.lazy(() =>
  import(/* webpackChunkName: '-dialog' */ './ComplianceBanner'),
);

const PrivacyBannerDialog = React.lazy(() =>
  import(/* webpackChunkName: '-dialog' */ './PrivacyBanner'),
);

const PremiumIntroBannerDialog = React.lazy(() =>
  import(/* webpackChunkName: '-dialog' */ './PremiumIntroBanner'),
);

const ConcurrentLoginBanner = React.lazy(() =>
  import(
    /* webpackChunkName: '-dialog' */ 'components/Banners/ConcurrentLogin'
  ),
);

const SubscriptionComparisonTable = React.lazy(() =>
  import(
    /* webpackChunkName: '-dialog' */ 'components/Subscriptions/ComparisonTable'
  ),
);

const HomeView = () => {
  const { t } = useTranslation();
  const {
    alertStore,
    classesStore,
    featuresStore,
    routerStore,
    uiStore,
    userStore,
    eventStore,
    premiumSessionsStore,
    organizationStore,
    configStore,
  } = useContext(storesContext);
  const { hasCurrentEnrollment, shouldShowNoEnrollmentBanner } = classesStore;
  const homeStore = useLocalStore(() => new HomeStore(routerStore, uiStore));
  const { isFreeTrialRegistrationEnabled } = configStore;
  const showComplianceBannerDialog = userStore.isComplianceBannerDue;
  const showPrivacyBannerDialog = userStore.needsPrivacyBanner;
  const showPremiumIntroBannerDialog = userStore.needsPremiumIntroBanner;
  const showFreeTrialOffering =
    isFreeTrialRegistrationEnabled &&
    !userStore.isUserAuthorized &&
    !userStore.hasUserAnyPastSubscription;
  const isSkillsPathsEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.SkillsPathsEnabled,
    defaultFlagValue: false,
  });
  const showLearningPathWidget =
    featuresStore.allFeaturesFetched && isSkillsPathsEnabled;

  const concurrentLoginAlert = eventStore.isConcurrentLogin
    ? {
        onDismiss: eventStore.resetConcurrentLogin,
      }
    : null;

  const [alerts, setAlerts] = useState([]);
  const removeAlert = (alertKey) => {
    setAlerts((oldList) => oldList.filter((a) => a.key !== alertKey));
  };

  const addAlert = (alert) => {
    setAlerts((oldList) => [...oldList, alert]);
  };

  const homeAlerts = alertStore.currentAlerts?.filter((alert) =>
    alert.environment.includes('home'),
  );

  const [isActiveUser, setActiveUser] = useLocalStorageState(
    'trialActiveUser',
    false,
  );

  useEffect(() => {
    if (
      routerStore.previousRoute?.name === 'trial-enroll' &&
      isActiveUser === true
    ) {
      let message = t(
        'Not eligible for the Red Hat Learning Subscription Trial',
      );
      let description = t(
        'You already have an active learning subscription with enhanced benefits. The Red Hat Learning Subscription Trial is unavailable with your current subscription',
      );
      if (userStore.isFreeTierSubscriber) {
        message = t('Red Hat Learning Subscription Trial is not yet available');
        description = t(
          'You have time remaining in your Red Hat Learning Subscription 14 day trial. Once the 14-day trial expires, you will then be able to access your Red Hat Learning Subscription Trial',
        );
      }

      addAlert({
        key: 'trial-active-user',
        message,
        variant: 'info',
        description,
      });
      setActiveUser(!isActiveUser);
    }
  }, [routerStore.previousRoute]);

  useEffect(() => {
    document.body.style.overflow = uiStore.isTourDisplayed
      ? 'hidden'
      : 'visible';
  }, [uiStore.isTourDisplayed]);

  useEffect(() => {
    if (premiumSessionsStore.loaded) {
      homeStore.premClassesWithEnrollments =
        classesStore.premiumClassesWithEnrollments;
    }
  }, [premiumSessionsStore.loaded]);

  if (showFreeTrialOffering) {
    return (
      <div className="container-grid-responsive">
        <FreeTrialOffering />
      </div>
    );
  }

  const showFreeTrialComparisonTable =
    isFreeTrialRegistrationEnabled &&
    classesStore.didFetchClassesForUser &&
    userStore.didFetchSubscription &&
    !userStore.isUserAuthorized &&
    userStore.hasUserAnyPastSubscription;

  return (
    <React.Fragment>
      <Helmet title="Red Hat Learning" />
      <VideoTourDisplay />
      <HeroCarousel />
      {!userStore.isAlaCarte && <SubscriptionWidget />}
      <div className="container-grid-responsive">
        {showComplianceBannerDialog ? <ComplianceBannerDialog /> : null}
        {showPrivacyBannerDialog ? <PrivacyBannerDialog /> : null}
        {showPremiumIntroBannerDialog ? <PremiumIntroBannerDialog /> : null}
        {(organizationStore.organizationsList || [])
          .filter((org) => org.is_pending)
          .map((org) => (
            <PendingManagerForm key={org.doc_id} organization={org} />
          ))}
        {eventStore.isConcurrentLogin ? (
          <ConcurrentLoginBanner {...concurrentLoginAlert} />
        ) : null}
        {shouldShowNoEnrollmentBanner ? <NoEnrollmentFound /> : null}
        {homeAlerts?.map((alert) => (
          <GenericAlert {...alert} />
        ))}
        {userStore?.isFreeTierSubscriber && !showFreeTrialComparisonTable && (
          <SubscriptionComparisonTable />
        )}
        {/* <SubscriptionComparisonTable resp="ls000" /> */}
        {/* <FreeTrialComparisonTable resp="???" /> */}
        {showFreeTrialComparisonTable ? (
          <div className="home-free-trial-wrapper">
            <FreeTrialComparisonTable />
            <h3>
              {t(`Want to continue learning? Upgrade to one of these tiers or`)}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={userStore.partnerUrl}
              >
                {' '}
                {t(`contact us`)}.
              </a>
            </h3>
          </div>
        ) : (
          <div className="widget-wrapper">
            <section className="my-widgets">
              <ClassesWidget classes={classesStore.iltClasses} modality="ilt" />
              <ClassesWidget classes={classesStore.vtClasses} modality="vt" />
              {userStore.isPremiumSubscriber && (
                <ClassesWidget
                  classes={classesStore.premiumClassesWithEnrollments}
                  modality="premvt"
                />
              )}
              {!hasCurrentEnrollment ? (
                <>
                  <AccessLearningSubscription />
                  <FreeSkillsAssessment />
                </>
              ) : (
                <>
                  <MyProgressWidget />
                  {userStore.isRHLSTrialSubscriber && <RHLSTrialWidget />}
                  {showLearningPathWidget && <LearningPathWidget />}
                </>
              )}
            </section>
            <div>
              <SubscriptionsVideo />
              <ExploreSubscriptions className="home-howtobuy" />
            </div>
          </div>
        )}

        <div>
          {alerts.length > 0 && <div className="trail-active-alert-overlay" />}
          <AlertGroup
            isToast
            isLiveRegion
            className="trail-active-user-group-alert"
          >
            {alerts.map((alert) => (
              <Alert
                variant={AlertVariant[alert.variant]}
                title={alert.message}
                actionClose={
                  <AlertActionCloseButton
                    title={alert.title}
                    onClose={() => removeAlert(alert.key)}
                  />
                }
                key={alert.key}
                actionLinks={
                  <React.Fragment>
                    <AlertActionLink // eslint-disable-next-line no-console
                      onClick={() => removeAlert(alert.key)}
                    >
                      {t(`Close`)}
                    </AlertActionLink>
                  </React.Fragment>
                }
              >
                {alert.description}
              </Alert>
            ))}
          </AlertGroup>
        </div>
      </div>
    </React.Fragment>
  );
};

export default withErrorBoundary(
  observer(HomeView),
  GenericViewError,
  handleError,
);
